import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { useDebounced } from './useDebounced.js';

/**
 * A paginated, filtered list.
 *
 * Every list screen needs the same three things — page state, filter state and
 * a query keyed on both — so they live here rather than being repeated.
 *
 * `keepPreviousData` means paging does not blank the table, which matters when
 * an admin is scanning down a list.
 */
export function useListQuery({ key, fetcher, initialFilters = {}, pageSize = 20, searchable = false }) {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(initialFilters);

  // Search lives in the hook rather than the screen, so debouncing cannot be
  // wired up as a setState during render.
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search);

  const effectiveFilters = useMemo(
    () => (searchable ? { ...filters, q: debouncedSearch } : filters),
    [filters, debouncedSearch, searchable],
  );

  const query = useQuery({
    queryKey: [key, page, effectiveFilters],
    queryFn: () => fetcher({ page, pageSize, ...cleanFilters(effectiveFilters) }),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  const setFilter = (name, value) => {
    setFilters((current) => ({ ...current, [name]: value }));
    // A changed filter invalidates the current page number.
    setPage(1);
  };

  // Reset from the event that causes it rather than from an effect watching the
  // debounced value — same result, one render instead of two.
  const updateSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  return {
    query,
    rows: query.data?.items || [],
    meta: query.data?.meta || {},
    page,
    setPage,
    filters,
    setFilter,
    search,
    setSearch: updateSearch,
    resetFilters: () => {
      setFilters(initialFilters);
      setSearch('');
      setPage(1);
    },
  };
}

/** Drops empty values so they are not sent as `?status=`. */
function cleanFilters(filters) {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value != null),
  );
}
